/**
 * Utility functions for processing rating breakdown data from the backend
 */
import type { IntlShape } from 'react-intl'
import { escapeRegex } from 'lib/string/regexUtils'

interface RatingDistributionItem {
  range: '9-10' | '7-9' | '5-7' | '3-5' | '1-3'
  count: number
  percentage: number
}

interface RatingBreakdownData {
  staffRating?: number
  facilitiesRating?: number
  valueRating?: number
  locationRating?: number
  accommodationRating?: number
  cleanlinessRating?: number
  mealsRating?: number
  recommendPercentage?: number
  ratingDistribution?: Array<RatingDistributionItem>
}

interface ReviewHighlightPart {
  text: string
  isHighlighted: boolean
}

/**
 * Formats rating range for display
 */
function formatRatingRange(range: string): string {
  if (range === '9-10') return '9.0+'
  if (range === '7-9') return '7.0-9.0'
  if (range === '5-7') return '5.0-7.0'
  if (range === '3-5') return '3.0-5.0'
  if (range === '1-3') return '1.0-3.0'
  return range
}

export function getReviewHighlightParts(text: string = '', searchTerm: string = ''): Array<ReviewHighlightPart> {
  const normalizedSearchTerm = searchTerm.trim()

  if (!normalizedSearchTerm) {
    return [{ text, isHighlighted: false }]
  }

  const search = new RegExp(`(${escapeRegex(normalizedSearchTerm)})`, 'ig')

  return text
    .split(search)
    .filter(part => part.length > 0)
    .map(part => ({
      text: part,
      isHighlighted: part.toLowerCase() === normalizedSearchTerm.toLowerCase(),
    }))
}

/**
 * Converts backend ratingDistribution to frontend format
 */
export function mapRatingDistribution(
  ratingDistribution: Array<RatingDistributionItem> | undefined,
  intl: IntlShape,
): Array<{ id: number; label: string; range: string; progress: number; count: number }> {
  if (!ratingDistribution || ratingDistribution.length === 0) {
    return []
  }

  // Sort by range order: 9-10, 7-9, 5-7, 3-5, 1-3
  const rangeOrder = ['9-10', '7-9', '5-7', '3-5', '1-3'] as const
  const sorted = [...ratingDistribution].sort((a, b) => {
    return rangeOrder.indexOf(a.range) - rangeOrder.indexOf(b.range)
  })

  return sorted.map((item, index) => ({
    id: index + 1,
    label: item.range === '9-10'
      ? intl.formatMessage({
          id: 'BLOsITRsnbPR5aCUYIkF',
          defaultMessage: 'Exceptional',
          description: 'Rating distribution label for 9-10 score band in the reviews section',
        })
      : item.range === '7-9'
        ? intl.formatMessage({
            id: 's8RpB2lHyep2-nwSwFZN',
            defaultMessage: 'Good',
            description: 'Rating distribution label for 7-9 score band in the reviews section',
          })
        : item.range === '5-7'
          ? intl.formatMessage({
              id: 'YtgSduaq4BESYFjINJk0',
              defaultMessage: 'Okay',
              description: 'Rating distribution label for 5-7 score band in the reviews section',
            })
          : item.range === '3-5'
            ? intl.formatMessage({
                id: 'VOTnb8v6ubpDS7AY19fR',
                defaultMessage: 'Poor',
                description: 'Rating distribution label for 3-5 score band in the reviews section',
              })
            : item.range === '1-3'
              ? intl.formatMessage({
                  id: 'GGDv0rZXAY4dGfbcejlu',
                  defaultMessage: 'Terrible',
                  description: 'Rating distribution label for 1-3 score band in the reviews section',
                })
              : item.range,
    range: formatRatingRange(item.range),
    progress: item.percentage,
    count: item.count,
  }))
}

/**
 * Converts backend rating breakdown to frontend format
 */
export function mapRatingBreakdown(
  breakdown: RatingBreakdownData | undefined,
  intl: IntlShape,
): Array<{ id: number; score: number; label: string }> {
  if (!breakdown) {
    return []
  }

  const items: Array<{ id: number; score: number; label: string }> = []

  if (breakdown.valueRating !== undefined) {
    items.push({
      id: 1,
      score: breakdown.valueRating,
      label: intl.formatMessage({
        id: 'ggoFqIDx0XzlTvxlFmAa',
        defaultMessage: 'Value',
        description: 'Rating breakdown category label for value in the reviews section',
      }),
    })
  }
  if (breakdown.facilitiesRating !== undefined && breakdown.facilitiesRating > 0) {
    items.push({
      id: 2,
      score: breakdown.facilitiesRating,
      label: intl.formatMessage({
        id: 'snWbvkKkwnhIK2qyncxD',
        defaultMessage: 'Facilities',
        description: 'Rating breakdown category label for facilities in the reviews section',
      }),
    })
  } else if (breakdown.cleanlinessRating !== undefined) {
    items.push({
      id: 2,
      score: breakdown.cleanlinessRating,
      label: intl.formatMessage({
        id: 'folu9mqxfoocIRu2hKKB',
        defaultMessage: 'Cleanliness',
        description: 'Rating breakdown category label for cleanliness in the reviews section',
      }),
    })
  }
  if (breakdown.staffRating !== undefined) {
    items.push({
      id: 3,
      score: breakdown.staffRating,
      label: intl.formatMessage({
        id: 'aMfzEALPhyqM2sf0x9EU',
        defaultMessage: 'Staff',
        description: 'Rating breakdown category label for staff in the reviews section',
      }),
    })
  }
  if (breakdown.locationRating !== undefined) {
    items.push({
      id: 4,
      score: breakdown.locationRating,
      label: intl.formatMessage({
        id: 'G53MJvOHMKgmuUxmXdz6',
        defaultMessage: 'Location',
        description: 'Rating breakdown category label for location in the reviews section',
      }),
    })
  }
  // Note: accommodationRating is intentionally excluded from display

  return items
}

/**
 * Gets recommend percentage text
 * @param recommendPercentage The recommend percentage (0-100)
 * @param entityType The type of entity: 'hotel', 'tour', or 'experience'
 */
export function getRecommendPercentageText(
  recommendPercentage: number | undefined,
  entityType: 'hotel' | 'tour' | 'experience' = 'hotel',
  intl: IntlShape,
): string | undefined {
  if (recommendPercentage === undefined || recommendPercentage === null) {
    return undefined
  }

  const entityLabel = entityType === 'tour'
    ? intl.formatMessage({
        id: 'QgGEkIim7Onw3zpGjECw',
        defaultMessage: 'this tour',
        description: 'Entity text used in recommend percentage sentence for tours',
      })
    : entityType === 'experience'
      ? intl.formatMessage({
          id: 'wORgTZBs7Xd7Bx7WickF',
          defaultMessage: 'this experience',
          description: 'Entity text used in recommend percentage sentence for experiences',
        })
      : intl.formatMessage({
          id: 'leb7XJLgc9fyuA9u7QXR',
          defaultMessage: 'this hotel',
          description: 'Entity text used in recommend percentage sentence for hotels',
        })

  return intl.formatMessage(
    {
      id: 'rw-5s8CX7odREUIQQRtU',
      defaultMessage: '{recommendPercentage}% recommend {entityLabel}',
      description: 'Sentence showing recommendation percentage and entity type',
    },
    {
      recommendPercentage,
      entityLabel,
    },
  )
}
